import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, ChevronRight, Check, FileText, Settings
} from 'lucide-react';
import {
  getProject, getDistributions, createDistribution, updateDistribution,
  getDistribution, getDistributionPlans, createDistributionPlan
} from '../../services/adminDealFlowApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/new-components/ui/tabs';
import { Input } from '../../components/new-components/ui/input';
import { Progress } from '../../components/new-components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const DIST_STATES = [
  { key: 'draft', label: 'Draft' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'authorized', label: 'Authorized' },
  { key: 'sent', label: 'Payments Sent' },
];

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<any>(null);
  const [distributions, setDistributions] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedDist, setSelectedDist] = useState<any>(null);
  const [distDetail, setDistDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNewDist, setShowNewDist] = useState(false);
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [newDist, setNewDist] = useState({ plan_id: '', payment_date: '', total_amount: '', name: '' });
  const [newPlan, setNewPlan] = useState({
    name: 'Operating Cashflow',
    remainder_allocation: 'withhold_remainder',
    classA: '8', classB: '10', classC: '12'
  });
  const [allocView, setAllocView] = useState<'allocations' | 'payments'>('allocations');

  const load = async () => {
    if (!id) return;
    try {
      const [projRes, distRes, planRes]: any[] = await Promise.all([
        getProject(id),
        getDistributions({ deal_id: id }),
        getDistributionPlans({ deal_id: id })
      ]);
      setProject(projRes.data.data);
      setDistributions(distRes.data.data || []);
      setPlans(planRes.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const loadDistDetail = async (distId: string) => {
    try {
      const res: any = await getDistribution(distId);
      setDistDetail(res.data.data);
      setSelectedDist(distId);
    } catch (err) { console.error(err); }
  };

  const handleCreateDist = async () => {
    try {
      await createDistribution({
        deal_id: id!,
        plan_id: newDist.plan_id || undefined,
        payment_date: newDist.payment_date,
        total_amount: Number(newDist.total_amount) || 0,
        name: newDist.name,
      });
      setShowNewDist(false);
      setNewDist({ plan_id: '', payment_date: '', total_amount: '', name: '' });
      load();
    } catch (err) { console.error(err); }
  };

  const handleCreatePlan = async () => {
    try {
      await createDistributionPlan({
        deal_id: id!,
        name: newPlan.name,
        remainder_allocation: newPlan.remainder_allocation,
        rules: [{
          hurdle: 'Preferred Return',
          classes: {
            A: Number(newPlan.classA),
            B: Number(newPlan.classB),
            C: Number(newPlan.classC)
          }
        }]
      });
      setShowNewPlan(false);
      load();
    } catch (err) { console.error(err); }
  };

  const handleAdvanceDist = async (distId: string, currentStatus: string) => {
    const nextMap: Record<string, string> = {
      draft: 'confirmed', confirmed: 'authorized', authorized: 'sent'
    };
    const next = nextMap[currentStatus];
    if (!next) return;
    try {
      await updateDistribution(distId, { status: next });
      load();
      if (selectedDist === distId) loadDistDetail(distId);
    } catch (err) { console.error(err); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!project) return <div className="text-muted-foreground text-center py-12">Project not found</div>;

  const chartData = distributions.filter(d => d.total_amount > 0).map(d => ({
    name: d.payment_date ? new Date(d.payment_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : 'N/A',
    amount: d.total_amount
  })).reverse();

  const capitalStack = project.classes?.map((c: any) => ({
    name: c.name,
    value: project.positions?.filter((p: any) => p.class_name === c.name)
      .reduce((sum: number, p: any) => sum + p.amount, 0) || 0
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-[#F0F0F0] rounded-lg flex items-center justify-center border border-border">
          <FileText className="h-6 w-6 text-muted-foreground" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold text-[#242424]">{project.title}</h1>
        </div>
      </div>

      <Link to="/admin/projects">
        <Button variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Projects
        </Button>
      </Link>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="positions">Positions</TabsTrigger>
          <TabsTrigger value="commitments">Commitments</TabsTrigger>
          <TabsTrigger value="capital-calls">Capital Calls</TabsTrigger>
          <TabsTrigger value="distributions">Distributions</TabsTrigger>
          <TabsTrigger value="classes">Classes</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="assets">Assets</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-border">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Total Capital Balance</p>
                  <p className="text-3xl font-display font-semibold text-[#242424]">{formatCurrency(project.total_capital)}</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Total Distributions</p>
                  <p className="text-3xl font-display font-semibold text-[hsl(var(--success))]">{formatCurrency(project.total_distributions)}</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Stakeholders</p>
                  <p className="text-3xl font-display font-semibold text-[hsl(var(--chart-2))]">{project.stakeholder_count}</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-[#242424] text-lg">Capital Stack</CardTitle>
                </CardHeader>
                <CardContent>
                  {capitalStack.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={capitalStack}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" stroke="#888" />
                        <YAxis stroke="#888" tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}K`} />
                        <Tooltip
                          contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}
                          formatter={(value: number) => formatCurrency(value)}
                        />
                        <Bar dataKey="value" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No capital data yet
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-[#242424] text-lg">Recent Distributions</CardTitle>
                </CardHeader>
                <CardContent>
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" stroke="#888" />
                        <YAxis stroke="#888" tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}K`} />
                        <Tooltip
                          contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}
                          formatter={(value: number) => formatCurrency(value)}
                        />
                        <Bar dataKey="amount" fill="hsl(var(--success))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No distributions yet
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Positions Tab */}
        <TabsContent value="positions">
          <Card className="border-border mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 text-xs text-muted-foreground">Account</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Class</th>
                    <th className="text-right p-4 text-xs text-muted-foreground">Amount</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Status</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Method</th>
                  </tr>
                </thead>
                <tbody>
                  {project.positions?.map((p: any) => (
                    <tr key={p.id} className="border-b border-border hover:bg-muted/50">
                      <td className="p-4 text-sm text-[#242424]">{p.email}</td>
                      <td className="p-4 text-sm text-muted-foreground">{p.class_name || 'Class A'}</td>
                      <td className="p-4 text-sm text-[#242424] text-right font-medium">{formatCurrency(p.amount)}</td>
                      <td className="p-4"><Badge variant="secondary" className="text-xs">{p.status}</Badge></td>
                      <td className="p-4 text-sm text-muted-foreground">{p.method || '—'}</td>
                    </tr>
                  )) || (
                    <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No positions</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Distributions Tab */}
        <TabsContent value="distributions">
          <div className="mt-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex gap-2">
                <Link to={`/admin/projects/${id}/distribution-plans`}>
                  <Button variant="outline">
                    <Settings className="h-4 w-4 mr-2" /> Distribution Plans
                  </Button>
                </Link>
              </div>
              <Button onClick={() => setShowNewDist(true)}>
                <Plus className="h-4 w-4 mr-2" /> New Distribution
              </Button>
            </div>

            <Card className="border-border">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 text-xs text-muted-foreground">Name</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Ex-Date</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Date</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">State</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Distribution Plan</th>
                      <th className="text-right p-4 text-xs text-muted-foreground">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {distributions.map((d: any) => (
                      <tr
                        key={d.id}
                        className="border-b border-border hover:bg-muted/50 cursor-pointer"
                        onClick={() => loadDistDetail(d.id)}
                      >
                        <td className="p-4 text-sm text-[#242424]">
                          {d.memo || `${d.payment_date ? new Date(d.payment_date).toISOString().split('T')[0] : ''} Distribution`}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {d.payment_date ? new Date(d.payment_date).toLocaleDateString() : '—'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {d.payment_date ? new Date(d.payment_date).toLocaleDateString() : '—'}
                        </td>
                        <td className="p-4">
                          <Badge variant={d.status === 'confirmed' ? 'default' : 'secondary'} className="text-xs">
                            {d.status}
                          </Badge>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{d.plan_name || 'Custom'}</td>
                        <td className="p-4 text-sm text-[#242424] text-right font-medium">
                          {formatCurrency(d.total_amount)}
                        </td>
                      </tr>
                    ))}
                    {distributions.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No distributions yet</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Distribution Detail */}
            {distDetail && (
              <Card className="border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-[#242424]">
                      {distDetail.memo || 'Distribution Detail'}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {distDetail.plan_name || 'Custom'} — {formatCurrency(distDetail.total_amount)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex gap-1">
                      {DIST_STATES.map((s, i) => {
                        const currentIdx = DIST_STATES.findIndex(x => x.key === distDetail.status);
                        return (
                          <div key={s.key} className="flex items-center">
                            <div className={`px-3 py-1 text-xs font-medium rounded ${
                              i <= currentIdx ? 'bg-primary/20 text-primary-foreground' : 'bg-muted text-muted-foreground'
                            }`}>
                              {s.label}
                            </div>
                            {i < DIST_STATES.length - 1 && (
                              <ChevronRight className="h-4 w-4 text-muted-foreground mx-1" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {distDetail.status !== 'sent' && (
                      <Button
                        size="sm"
                        onClick={() => handleAdvanceDist(distDetail.id, distDetail.status)}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        {distDetail.status === 'draft' ? 'Confirm' :
                         distDetail.status === 'confirmed' ? 'Authorize' : 'Send Payments'}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Date</p>
                      <p className="text-[#242424]">{distDetail.payment_date ? new Date(distDetail.payment_date).toLocaleDateString() : '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Distribution</p>
                      <p className="text-[#242424] font-semibold">{formatCurrency(distDetail.total_amount)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Cash Distribution</p>
                      <p className="text-[#242424] font-semibold">{formatCurrency(distDetail.total_amount)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Reinvested</p>
                      <p className="text-[#242424]">$0.00</p>
                    </div>
                  </div>

                  <div className="flex gap-2 mb-4">
                    <Button
                      size="sm"
                      variant={allocView === 'allocations' ? 'default' : 'outline'}
                      onClick={() => setAllocView('allocations')}
                    >
                      Allocations
                    </Button>
                    <Button
                      size="sm"
                      variant={allocView === 'payments' ? 'default' : 'outline'}
                      onClick={() => setAllocView('payments')}
                    >
                      Payments
                    </Button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                      <thead>
                        <tr className="border-b border-border">
                          {allocView === 'allocations' ? (
                            <>
                              <th className="text-left p-3 text-xs text-muted-foreground">Account</th>
                              <th className="text-left p-3 text-xs text-muted-foreground">Position #</th>
                              <th className="text-left p-3 text-xs text-muted-foreground">Unit Class</th>
                              <th className="text-left p-3 text-xs text-muted-foreground">Hurdle</th>
                              <th className="text-left p-3 text-xs text-muted-foreground">Classification</th>
                              <th className="text-right p-3 text-xs text-muted-foreground">Amount</th>
                              <th className="text-right p-3 text-xs text-muted-foreground">Deductions</th>
                            </>
                          ) : (
                            <>
                              <th className="text-left p-3 text-xs text-muted-foreground">Account</th>
                              <th className="text-left p-3 text-xs text-muted-foreground">Position #</th>
                              <th className="text-left p-3 text-xs text-muted-foreground">Custodian</th>
                              <th className="text-right p-3 text-xs text-muted-foreground">Amount</th>
                              <th className="text-right p-3 text-xs text-muted-foreground">Deductions</th>
                              <th className="text-right p-3 text-xs text-muted-foreground">Net Payment</th>
                              <th className="text-left p-3 text-xs text-muted-foreground">Status</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {distDetail.entries?.map((e: any) => (
                          <tr key={e.id} className="border-b border-border hover:bg-muted/50">
                            {allocView === 'allocations' ? (
                              <>
                                <td className="p-3 text-sm text-[#242424]">{e.account_name || e.first_name_enc || '—'}</td>
                                <td className="p-3 text-sm text-muted-foreground">{e.position_number}</td>
                                <td className="p-3 text-sm text-muted-foreground">{e.unit_class || 'Class A'}</td>
                                <td className="p-3 text-sm text-muted-foreground">{e.hurdle || 'Preferred Return'}</td>
                                <td className="p-3 text-sm text-muted-foreground">{e.classification || 'Preferred'}</td>
                                <td className="p-3 text-sm text-[#242424] text-right">{formatCurrency(e.amount)}</td>
                                <td className="p-3 text-sm text-[#242424] text-right">{formatCurrency(e.deductions)}</td>
                              </>
                            ) : (
                              <>
                                <td className="p-3 text-sm text-[#242424]">{e.account_name || e.first_name_enc || '—'}</td>
                                <td className="p-3 text-sm text-muted-foreground">{e.position_number}</td>
                                <td className="p-3 text-sm text-muted-foreground">—</td>
                                <td className="p-3 text-sm text-[#242424] text-right">{formatCurrency(e.amount)}</td>
                                <td className="p-3 text-sm text-[#242424] text-right">{formatCurrency(e.deductions)}</td>
                                <td className="p-3 text-sm text-[#242424] text-right font-semibold">{formatCurrency(e.net_payment)}</td>
                                <td className="p-3">
                                  <Badge variant="secondary" className="text-xs">{e.status}</Badge>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                        {(!distDetail.entries || distDetail.entries.length === 0) && (
                          <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No allocation entries</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Placeholder tabs */}
        {['transactions', 'commitments', 'capital-calls', 'classes', 'documents', 'assets', 'payments', 'settings'].map(tab => (
          <TabsContent key={tab} value={tab}>
            <div className="mt-4 text-center py-12 text-muted-foreground">
              <p>{tab.charAt(0).toUpperCase() + tab.slice(1).replace('-', ' ')} management coming soon</p>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* New Distribution Dialog */}
      <Dialog open={showNewDist} onOpenChange={setShowNewDist}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>New Distribution</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button size="sm">Distribution Plan</Button>
              <Button size="sm" variant="outline">Custom</Button>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Distribution Plan</label>
              <Select value={newDist.plan_id} onValueChange={v => setNewDist(p => ({ ...p, plan_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select plan..." />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((pl: any) => (
                    <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Distribution Date</label>
              <Input
                type="date"
                value={newDist.payment_date}
                onChange={e => setNewDist(p => ({ ...p, payment_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Distributable Amount ($)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  value={newDist.total_amount}
                  onChange={e => setNewDist(p => ({ ...p, total_amount: e.target.value }))}
                  className="pl-8"
                  placeholder="Enter total amount to distribute"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Name (optional)</label>
              <Input
                value={newDist.name}
                onChange={e => setNewDist(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Q2 Distribution"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDist(false)}>Cancel</Button>
            <Button onClick={handleCreateDist}>Create Distribution</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
